/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { RegionsHttpService } from "./http/regions-http.service";
import { Observable } from "rxjs";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import { ChartResponse } from "../models/ui/charts.model";

@Injectable({
	providedIn: "root"
})
export class RegionsService {
	constructor(private _regionsHttpService: RegionsHttpService) {}

	/**
	 * Retrieves the average carbon intensity for a given tenant within a specified date range.
	 *
	 * @param startDate - The start date of the period for which to calculate the average carbon intensity (in YYYY-MM-DD format).
	 * @param endDate - The end date of the period for which to calculate the average carbon intensity (in YYYY-MM-DD format).
	 * @param tenantId - The unique identifier of the tenant for which to retrieve the average carbon intensity.
	 * @returns An Observable that emits a ChartResponse containing a BarChart with the average carbon intensity data.
	 */
	public getAverageCarbonIntensity(
		startDate: string,
		endDate: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		return this._regionsHttpService.getAverageCarbonIntensity(startDate, endDate, tenantId);
	}

	/**
	 * Retrieves the carbon intensity data for a specific region within a given date range.
	 *
	 * @param region - The name of the region for which to retrieve carbon intensity data.
	 * @param startDate - The start date of the date range in ISO format (YYYY-MM-DD).
	 * @param endDate - The end date of the date range in ISO format (YYYY-MM-DD).
	 * @returns An Observable that emits a ChartResponse containing LineChart data for the specified region and date range.
	 */
	public getCarbonIntensityByRegion(
		region: string,
		startDate: string,
		endDate: string
	): Observable<ChartResponse<LineChart>> {
		return this._regionsHttpService.getCarbonIntensityByRegion(region, startDate, endDate);
	}
}
